import { useState, useMemo, useCallback, useEffect } from "react";
import {
    Button,
    Collapse,
    Form,
    FormGroup,
    Input,
    Label,
    Nav,
    Navbar,
    NavbarBrand,
    NavbarToggler,
    NavItem,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    UncontrolledTooltip,
} from "reactstrap";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faSun,
    faMoon,
    faHome,
    faMagnifyingGlass,
    faClock,
    faUsers,
    faGear,
    faWrench,
    faCircleQuestion,
    faShield,
} from "@fortawesome/free-solid-svg-icons";

import { defaultContext } from "components/UserContextProvider";

import styles from "components/NavMenu/NavMenu.module.css";

import useAlertContext from "Hooks/useAlertContext";
import useHelpers from "Hooks/useHelpers";
import useFetch from "Hooks/useFetch";
import useUserContext from "Hooks/useUserContext";
import SystemLinksDropdown from "components/NavMenu/Components/SystemLinksDropdown/SystemLinksDropdown";
import ProcoreStatus from "components/NavMenu/Components/ProcoreStatus/ProcoreStatus";
import AgentFileDownloader from "components/NavMenu/Components/AgentFileDownloader/AgentFileDownloader";
import MatchToolsLinks from "components/NavMenu/Components/MatchToolsLinks/MatchToolsLinks";
import SystemStatusMessages from "components/NavMenu/Components/SystemStatusMessages/SystemStatusMessages";
import IconButton from "./Components/SystemStatusMessages/Components/IconButton/IconButton";
import NavDropdownItem from "components/NavMenu/Components/SystemStatusMessages/Components/NavDropDownItem/NavDropdownItem";
import useCompanyLogos from "Hooks/useCompanyLogos";
import useUploadLogo from "Hooks/useUploadLogo";
import useTheme from "Hooks/useTheme";

interface IConfig {
    cmicUrl: string;
    documentationUrl: string;
    [key: string]: any;
}

/** @component the NavMenu/NavBar
 * @see subcomponents {@link SystemLinksDropdown}, {@link AgentFileDownloader, @link MatchToolsLinks}
 * @see hooks {@link useUploadLogo}
 */
export default function NavMenu() {
    /** Controls state of NavBarToggle (collapsible menu for narrower screens) */
    const [collapsed, setCollapsed] = useState(true);
    /** Controls state of password change modal open/closed */
    const [isModalOpen, setIsModalOpen] = useState(false);
    /** Controls state of preferences modal open/closed */
    const [isPrefModalOpen, setIsPrefModalOpen] = useState(false);
    /** Used to display validation/error messages in the password change modal */
    const [passwordChanged, setPasswordChanged] = useState<string | null>(null);
    /** Holds some URLs that are fetched */
    const [config, setConfig] = useState<IConfig>({
        cmicUrl: "",
        documentationUrl: "",
    });
    const [userContext, onSetUserContext] = useUserContext();
    const { newAlert } = useAlertContext();
    const { getLogo, onImageError } = useCompanyLogos();
    const { isLogoChanged, selectLogoToUpload, selectDarkLogoToUpload, hiddenUploadLogoFileInput } =
        useUploadLogo();
    const { theme, toggleTheme } = useTheme();

    const navigate = useNavigate();
    const location = useLocation();

    const form = useFormik({
        initialValues: {
            password: "",
            confirmPassword: "",
        },
        validate,
        onSubmit: changePassword,
    });

    const { isAdmin } = useHelpers();
    const { get, post, put } = useFetch();

    // Fetch some configuration values used for cmicUrl and clientSpecsUrl below
    useEffect(() => {
        if (!userContext.isAuthenticated) {
            return;
        }

        let ignore = false;

        getConfigs();

        async function getConfigs() {
            if (ignore || !userContext.companyId) return;

            const response = await get(
                `/api/configuration/${userContext.companyId}`
            );

            if (response.ok && !ignore) {
                const data: IConfig = await response.json();

                const cmicUrlItem = data.find(
                    (c: { keyName: string; keyValue: string }) =>
                        c.keyName.toLowerCase().includes("cmic.rpa.launch_page")
                );

                const documentationUrlItem = data.find(
                    (c: { keyName: string; keyValue: string }) =>
                        c.keyName.toLowerCase().includes("documentationurl")
                );

                setConfig({
                    cmicUrl: cmicUrlItem?.keyValue ?? "",
                    documentationUrl: documentationUrlItem?.keyValue,
                });
            }

            return () => {
                ignore = true;
            };
        }
    }, [get, userContext.companyId, userContext.isAuthenticated]);

    // Ensure the dropdown version of the navbar is closed when the route changes
    useEffect(() => {
        setCollapsed(true);
    }, [location]);

    /** Toggles collapsed state for the navbar menu that appears on narrower screens */
    function toggleNavbar() {
        setCollapsed((collapsed) => !collapsed);
    }

    /** Toggles the change password modal */
    const toggleModal = useCallback(() => {
        form.resetForm();
        setPasswordChanged(null);
        setIsModalOpen((isModalOpen) => !isModalOpen);
    }, [form]);

    /** Toggles the preferences modal */
    const togglePrefModal = useCallback(() => {
        setIsPrefModalOpen((open) => !open);
    }, []);

    /** Current value of the "run home-page jobs inline" preference (default off) */
    const runInline = userContext.preferences?.run_inline === "true";
    /** Current value of the "Use New Launch Page" preference (default off). */
    const useNewLaunchPage =
        userContext.preferences?.use_new_launch_page === "true";

    /** Persists a single preference for the current user, then updates context locally */
    const setPreference = useCallback(
        async (key: string, value: string) => {
            const response = await put(`/api/me/preferences/${key}`, { value });
            if (response.ok) {
                onSetUserContext({
                    preferences: { ...userContext.preferences, [key]: value },
                });
                newAlert({ color: "success", message: "Preference saved" });
            } else {
                newAlert({
                    color: "danger",
                    message: "Failed to save preference",
                });
            }
        },
        [put, onSetUserContext, userContext.preferences, newAlert]
    );

    // ===== Change Password =====
    /** Change password validation function. Used with the Formik NPM library. */
    function validate(values: { [field: string]: any }) {
        const errors: { [field: string]: string } = {};
        // Password must contain at least one number, one uppercase letter, and one lowercase letter and be 8-16 characters in length
        const regex = /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z]).{8,16}$/;

        if (!values.password.length) {
            errors.password = "Password is required";
        } else if (values.password.length < 8 || values.password.length > 16) {
            errors.password =
                "Password must be between 8 and 16 characters in length";
        } else if (!regex.test(values.password)) {
            errors.password =
                "Password must contain at least one number, one uppercase letter, and one lowercase letter";
        }
        if (values.confirmPassword !== values.password) {
            errors.confirmPassword = "Passwords do not match";
        }
        return errors;
    }

    /** Fetch to change the user's password. Fires on submitting the form in the modal. */
    async function changePassword(values: {
        password: string;
        confirmPassword: string;
    }) {
        console.log({
            ...form.values,
            user_id: userContext.userId,
        });

        const body = {
            ...form.values,
            user_id: userContext.userId,
        };

        const response = await post("/api/users/change_password", body);

        if (response.ok) {
            setPasswordChanged("Password changed successfully.");
        } else {
            setPasswordChanged(
                "Password change failed. Please try again or contact Calance."
            );
        }
    }

    /** Displays demo/uat/prod environment with color based on hub url */
    const environmentBar = useMemo(() => {
        let environmentBoxStyle;
        let text;
        switch (userContext.environment) {
            case "uat":
                environmentBoxStyle = { backgroundColor: "var(--bs-primary)" };
                text = "User Test Environment";
                break;
            case "prod":
                environmentBoxStyle = { backgroundColor: "var(--bs-success)" };
                text = "Production Environment";
                break;
            case "demo":
                environmentBoxStyle = {
                    backgroundColor: "var(--bs-warning)",
                };
                text = "Demo Environment";
                break;
            case "local":
                text = "Local";
                break;
            default:
                environmentBoxStyle = {
                    backgroundColor: "var(--dimensionDarkGray)",
                };
                text = "";
        }

        return (
            <div className={styles.environmentBox} style={environmentBoxStyle}>
                {userContext.environment && text}
            </div>
        );
    }, [userContext.environment]);

    /** Conditionally rendered navbar menu content / links based on user's permissions */
    const navItems = useMemo(() => {
        const systems = userContext.companySystems ?? "";
        const systemsLower = systems.toLowerCase();
        const permissions = userContext.permissions ?? "";
        const companies = userContext.companies ?? "";
        const hasRamp = systemsLower.includes("ramp");
        const hasCmic = systemsLower.includes("cmic");
        const hasMri = systemsLower.includes("mrix") || systemsLower.includes("mri");
        const hasNetSuite = systemsLower.includes("netsuite");
        const hasSpectrum = systemsLower.includes("spectrum");
        const hasJde = systemsLower.includes("jde");

        let setupGuideUrl = "";
        if (systems.includes("JDE") && systems.includes("Procore")) {
            setupGuideUrl =
                "https://docs.google.com/document/d/1ETAWRju86xTe73NWSefusdhSo7dz3eLM3FBrkSorc6Y";
        } else if (systems.includes("MRIX") && systems.includes("Procore")) {
            setupGuideUrl =
                "https://docs.google.com/document/d/1E37fqcQXc2LxB1TkXLpGm7ob80sn33wLCxqg4t35spQ";
        } else if (
            systems.includes("CMICACCTG") &&
            systems.includes("Procore")
        ) {
            setupGuideUrl =
                "https://docs.google.com/document/d/1yLj1bQu8eNv0iQFSZ8NOgP-dQ2tIgGSp2cSW4hsa_UQ/edit#heading=h.f0w29ot3lw92";
        } else if (systems.includes("CMIC") && systems.includes("Procore")) {
            setupGuideUrl =
                "https://docs.google.com/document/d/1yLj1bQu8eNv0iQFSZ8NOgP-dQ2tIgGSp2cSW4hsa_UQ/edit#heading=h.f0w29ot3lw92";
        }

        async function logout() {
            const response = await post("/api/logout");
            if (response.ok) {
                onSetUserContext(defaultContext);
                navigate("/login");
            } else {
                newAlert({ message: "Error logging out" });
            }
        }

        if (userContext.isAuthenticated) {
            return (
                <>
                    <Nav className={styles.linksBox}>
                        <NavItem>
                            <NavLink className={styles.link} to="/integrations">
                                <FontAwesomeIcon icon={faHome} className={styles.navIcon} />
                                <span className={styles.navLabel}>Launch</span>
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink className={styles.link} to="/run" end>
                                <FontAwesomeIcon icon={faMagnifyingGlass} className={styles.navIcon} />
                                <span className={styles.navLabel}>Review</span>
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                to={"/run_details/latest"}
                                className={styles.link}
                            >
                                <FontAwesomeIcon icon={faClock} className={styles.navIcon} />
                                <span className={styles.navLabel}>Latest</span>
                            </NavLink>
                        </NavItem>
                        {/* Users Dropdown Menu */}
                        {(permissions.Users || isAdmin()) && (
                            <UncontrolledDropdown nav inNavbar>
                                <DropdownToggle
                                    className={styles.link}
                                    nav
                                    caret
                                >
                                    <FontAwesomeIcon icon={faUsers} className={styles.navIcon} />
                                    <span className={styles.navLabel}>Users</span>
                                </DropdownToggle>
                                <DropdownMenu>
                                    {companies > 1 && (
                                        <DropdownItem
                                            tag={NavLink}
                                            className={styles.link}
                                            to="/login_company"
                                        >
                                            Change Company
                                        </DropdownItem>
                                    )}
                                    <DropdownItem
                                        tag={NavLink}
                                        to={"/users/roles"}
                                        className={styles.link}
                                    >
                                        Manage Roles
                                    </DropdownItem>
                                    <DropdownItem
                                        tag={NavLink}
                                        to={"/users/manage"}
                                        className={styles.link}
                                    >
                                        Manage Users
                                    </DropdownItem>
                                </DropdownMenu>
                            </UncontrolledDropdown>
                        )}

                        {/* Setup Dropdown Menu */}
                        <UncontrolledDropdown nav inNavbar>
                            <DropdownToggle className={styles.link} nav caret>
                                <FontAwesomeIcon icon={faGear} className={styles.navIcon} />
                                <span className={styles.navLabel}>Setup</span>
                            </DropdownToggle>
                            <DropdownMenu>
                                {(isAdmin() || permissions.Config) && (
                                    <DropdownItem
                                        tag={NavLink}
                                        to={"/setup/configuration"}
                                        className={styles.link}
                                    >
                                        Configuration
                                    </DropdownItem>
                                )}
                                {isAdmin() && hasRamp && (hasCmic || hasMri || hasSpectrum || hasJde) && (
                                    <DropdownItem
                                        tag={NavLink}
                                        to={"/setup/connect"}
                                        className={styles.link}
                                    >
                                        Connect Systems
                                    </DropdownItem>
                                )}
                                {/* company.systems casing is inconsistent per row
                                    ("Billy|Intacct" vs "billy|INTACCT"), so match
                                    case-insensitively as ProcoreStatus and
                                    SystemLinksDropdown already do. */}
                                {systems.toLowerCase().includes("billy") &&
                                    systems.toLowerCase().includes("intacct") && (
                                    <DropdownItem
                                        tag={NavLink}
                                        to={"/setup/connect_billy"}
                                        className={styles.link}
                                    >
                                        Connect Systems
                                    </DropdownItem>
                                )}
                                {isAdmin() && (
                                    <>
                                        <AgentFileDownloader
                                            type={"Agent Configuration"}
                                        />
                                        <AgentFileDownloader type={"Agent"} arch={"x64"} />
                                        <AgentFileDownloader type={"Agent"} arch={"x86"} />
                                    </>
                                )}
                                {(isAdmin() || permissions.Connection) && (
                                    <DropdownItem
                                        tag={NavLink}
                                        to={"/setup/test_connection"}
                                        className={styles.link}
                                    >
                                        Test a Connection
                                    </DropdownItem>
                                )}
                                {isAdmin() && (
                                    <DropdownItem
                                        tag={NavLink}
                                        to={""}
                                        className={styles.link}
                                        onClick={selectLogoToUpload}
                                    >
                                        Upload Logo
                                    </DropdownItem>
                                )}
                                {isAdmin() && (
                                    <DropdownItem
                                        tag={NavLink}
                                        to={""}
                                        className={styles.link}
                                        onClick={selectDarkLogoToUpload}
                                    >
                                        Upload Logo (Dark)
                                    </DropdownItem>
                                )}
                                {(isAdmin() || permissions.EditSchedule) && (
                                    <DropdownItem
                                        tag={NavLink}
                                        to={"/setup/scheduler"}
                                        className={styles.link}
                                    >
                                        Schedule
                                    </DropdownItem>
                                )}
                            </DropdownMenu>
                        </UncontrolledDropdown>

                        {/* Tools Dropdown Menu */}
                        <UncontrolledDropdown nav inNavbar>
                            <DropdownToggle className={styles.link} nav caret>
                                <FontAwesomeIcon icon={faWrench} className={styles.navIcon} />
                                <span className={styles.navLabel}>Tools</span>
                            </DropdownToggle>
                            <DropdownMenu>
                                {isAdmin() && (
                                    <DropdownItem
                                        tag={NavLink}
                                        to={"/tools/email_rules"}
                                        className={styles.link}
                                    >
                                        Email Rules
                                    </DropdownItem>
                                )}
                                {systems.includes("CMICACCTG") && (
                                    <DropdownItem
                                        tag={NavLink}
                                        to={config.cmicUrl}
                                        className={styles.link}
                                    >
                                        Go to CMIC
                                    </DropdownItem>
                                )}
                                <DropdownItem
                                    tag={NavLink}
                                    to={"/tools/message_search"}
                                    className={styles.link}
                                >
                                    Message Search
                                </DropdownItem>
                                {(isAdmin() ||
                                    userContext.permissions.MatchTools) && (
                                        <MatchToolsLinks />
                                    )}
                                {hasNetSuite && userContext.isCalanceAdmin === "1" &&(
                                    <DropdownItem
                                        tag={NavLink}
                                        to={"/tools/suiteql_search"}
                                        className={styles.link}
                                    >
                                        SuiteQL Search
                                    </DropdownItem>
                                )}
                            </DropdownMenu>
                        </UncontrolledDropdown>

                        {/* Help Dropdown Menu */}
                        <UncontrolledDropdown nav inNavbar>
                            <DropdownToggle className={styles.link} nav caret>
                                <FontAwesomeIcon icon={faCircleQuestion} className={styles.navIcon} />
                                <span className={styles.navLabel}>Help</span>
                            </DropdownToggle>
                            <DropdownMenu>
                                {config.documentationUrl && (
                                    <DropdownItem
                                        tag={NavLink}
                                        to={config.documentationUrl}
                                        target="_blank"
                                        className={styles.link}
                                    >
                                        Client Specifications
                                    </DropdownItem>
                                )}
                                <DropdownItem
                                    tag={NavLink}
                                    to="https://docs.google.com/document/d/1BqNnRSZnQevAJfG0dXUS-gvOD1PIl1q9P1qkoPr79Hs/edit?tab=t.0#heading=h.f3qu99qq77j6"
                                    target="_blank"
                                    className={styles.link}
                                >
                                    Welcome Guide
                                </DropdownItem>
                                <DropdownItem
                                    tag={NavLink}
                                    to="https://docs.google.com/document/d/1wYY8XWkbCR1nIeulrMkdvCOFcG-r5-ah8SGV5khg1-w/edit?usp=sharing"
                                    target="_blank"
                                    className={styles.link}
                                >
                                    Data Agent Guide
                                </DropdownItem>
                                <DropdownItem
                                    tag={NavLink}
                                    to="mailto:integration@calance.com?subject=Dimension Support"
                                    className={styles.link}
                                >
                                    Email Support
                                </DropdownItem>
                                <DropdownItem
                                    tag={NavLink}
                                    to="https://docs.google.com/document/d/1yfSU7W-DZmkQ91nE8tB-0CZQMwiJHTwGrDmFK83x3Uc"
                                    target="_blank"
                                    className={styles.link}
                                >
                                    Portal User Guide
                                </DropdownItem>
                                {setupGuideUrl !== "" && (
                                    <DropdownItem
                                        tag={NavLink}
                                        to={setupGuideUrl}
                                        target="_blank"
                                        className={styles.link}
                                    >
                                        Setup Guide
                                    </DropdownItem>
                                )}
                                <DropdownItem
                                    tag={NavLink}
                                    to="https://2-app.donedone.com/"
                                    target="_blank"
                                    className={styles.link}
                                >
                                    Support Tickets
                                </DropdownItem>
                                <SystemStatusMessages
                                    displayComponent={NavDropdownItem}
                                    showReadMessages
                                />
                            </DropdownMenu>
                        </UncontrolledDropdown>

                        {/* Calance Admin menu */}
                        {userContext.isCalanceAdmin === "1" && (
                            <UncontrolledDropdown nav inNavbar>
                                <DropdownToggle
                                    className={styles.link}
                                    nav
                                    caret
                                >
                                    <FontAwesomeIcon icon={faShield} className={styles.navIcon} />
                                    <span className={styles.navLabel}>Admin</span>
                                </DropdownToggle>
                                <DropdownMenu>
                                    <DropdownItem
                                        tag={NavLink}
                                        to="/admin/agent_status"
                                        className={styles.link}
                                    >
                                        Agent Status
                                    </DropdownItem>
                                    <DropdownItem
                                        tag={NavLink}
                                        to="/admin/client_health/summary"
                                        className={styles.link}
                                    >
                                        Client Health
                                    </DropdownItem>
                                    <DropdownItem
                                        tag={NavLink}
                                        to="/admin/map"
                                        className={styles.link}
                                    >
                                        Client Map
                                    </DropdownItem>
                                    <DropdownItem
                                        tag={NavLink}
                                        to="/admin/company"
                                        className={styles.link}
                                    >
                                        Company Setup
                                    </DropdownItem>
                                    <DropdownItem
                                        tag={NavLink}
                                        to="/admin/config"
                                        className={styles.link}
                                    >
                                        Configuration
                                    </DropdownItem>
                                    <DropdownItem
                                        tag={NavLink}
                                        to="/admin/edit_integrations"
                                        className={styles.link}
                                    >
                                        Edit Integrations
                                    </DropdownItem>
                                    <DropdownItem
                                        tag={NavLink}
                                        to="/admin/edit_match_tools"
                                        className={styles.link}
                                    >
                                        Edit Match Tools
                                    </DropdownItem>
                                    <DropdownItem
                                        tag={NavLink}
                                        to="/admin/login_as"
                                        className={styles.link}
                                    >
                                        Login As
                                    </DropdownItem>
                                    <DropdownItem
                                        tag={NavLink}
                                        to="/admin/status_messages"
                                        className={styles.link}
                                    >
                                        System Status Messages
                                    </DropdownItem>
                                    <DropdownItem
                                        tag={NavLink}
                                        to="/admin/stuck_integrations"
                                        className={styles.link}
                                    >
                                        Unfinished Integrations
                                    </DropdownItem>
                                    <DropdownItem
                                        tag={NavLink}
                                        to="/admin/stuck_jobs"
                                        className={styles.link}
                                    >
                                        Unfinished Jobs
                                    </DropdownItem>
                                    <DropdownItem
                                        tag={NavLink}
                                        to="/admin/company_methods"
                                        className={styles.link}
                                    >
                                        View Method Details
                                    </DropdownItem>
                                </DropdownMenu>
                            </UncontrolledDropdown>
                        )}
                        <SystemStatusMessages displayComponent={IconButton} />
                    </Nav>

                    {/* Procore / Other Systems Icons & Username Dropdown */}
                    <Nav className={styles.usernameArea}>
                        {/* Theme Toggle — slider with sun + moon icons. Pulled out
                            of .secondaryNav so it stays visible at narrow widths
                            (where .secondaryNav is display:none); the slider
                            itself shrinks via .themeToggleItem media query. */}
                        <NavItem className={styles.themeToggleItem}>
                            <FontAwesomeIcon icon={faSun} className={styles.themeIcon} />
                            <button
                                id="theme-toggle"
                                type="button"
                                className={`${styles.themeSlider} ${theme === "dark" ? styles.themeSliderDark : ""}`}
                                onClick={toggleTheme}
                                aria-label="Toggle theme"
                            >
                                <span className={styles.themeSliderKnob} />
                            </button>
                            <UncontrolledTooltip placement="bottom" target="theme-toggle">
                                {theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
                            </UncontrolledTooltip>
                            <FontAwesomeIcon icon={faMoon} className={styles.themeIcon} />
                        </NavItem>
                        <div className={styles.secondaryNav}>

                            {/* Procore Status Indicator */}
                            <ProcoreStatus />

                            {/* Procore / Other Systems Icons */}
                            <SystemLinksDropdown />
                        </div>

                        {/* Username dropdown */}
                        <UncontrolledDropdown
                            nav
                            inNavbar
                            className={styles.userDropdown}
                        >
                            <DropdownToggle
                                className={`${styles.link} me-4`}
                                nav
                                caret
                            >
                                {userContext.username.includes("@")
                                    ? userContext.username.substring(
                                          0,
                                          userContext.username.indexOf("@")
                                      )
                                    : userContext.username}
                            </DropdownToggle>
                            <DropdownMenu className={styles.menuRight}>
                                {companies > 1 && (
                                    <DropdownItem
                                        tag={NavLink}
                                        className={styles.link}
                                        to="/login_company"
                                    >
                                        Change Company
                                    </DropdownItem>
                                )}
                                <DropdownItem
                                    tag={NavLink}
                                    to={""}
                                    className={styles.link}
                                    onClick={togglePrefModal}
                                >
                                    Preferences
                                </DropdownItem>
                                <DropdownItem
                                    tag={NavLink}
                                    to={""}
                                    className={styles.link}
                                    onClick={toggleModal}
                                >
                                    Change Password
                                </DropdownItem>
                                <DropdownItem
                                    tag={NavLink}
                                    to={""}
                                    className={styles.link}
                                    onClick={logout}
                                >
                                    Log Out
                                </DropdownItem>
                            </DropdownMenu>
                        </UncontrolledDropdown>
                    </Nav>
                </>
            );
        }
    }, [
        userContext.companySystems,
        userContext.permissions,
        userContext.companies,
        userContext.isAuthenticated,
        userContext.isCalanceAdmin,
        userContext.username,
        post,
        onSetUserContext,
        navigate,
        newAlert,
        isAdmin,
        selectLogoToUpload,
        selectDarkLogoToUpload,
        config.cmicUrl,
        config.documentationUrl,
        toggleModal,
        togglePrefModal,
        theme,
        toggleTheme,
    ]);

    /** Logo on the left side of the navbar */
    const navBarLogo = useMemo(
        () =>
            userContext.isAuthenticated &&
            !window.location.href.includes("/login_company") ? (
                <>
                    <img
                        id="logo"
                        className={styles.brandLogo}
                        src={`${getLogo(userContext.companyId)}${isLogoChanged ? `?t=${isLogoChanged}` : ""}`}
                        onError={(e) => onImageError(e)}
                        alt={`${userContext.companyId} Logo`}
                    />
                    <UncontrolledTooltip placement="right" target="logo">
                        {userContext.companyId.toUpperCase()} -{" "}
                        {userContext.companyName}
                    </UncontrolledTooltip>
                </>
            ) : null,
        [
            userContext.isAuthenticated,
            userContext.companyId,
            userContext.companyName,
            isLogoChanged,
            getLogo,
            onImageError,
            theme,
        ]
    );

    return (
        <header>
            <Navbar expand="sm" className={styles.navBar} light>
                <NavbarBrand className={styles.brand}>{navBarLogo}</NavbarBrand>
                <NavbarToggler
                    className={styles.toggler}
                    onClick={toggleNavbar}
                />
                <Collapse
                    className={styles.collapse}
                    isOpen={!collapsed}
                    navbar
                >
                    {/* Large amount of conditionally rendered links based on user's permissions */}
                    {navItems}
                </Collapse>
            </Navbar>

            {/* Displays demo/uat/prod environment with color based on hub url */}
            {environmentBar}

            {/* Change password modal */}
            <Modal
                className={styles.modal}
                isOpen={isModalOpen}
                toggle={toggleModal}
            >
                <ModalHeader>Change Password</ModalHeader>
                <Form onSubmit={form.handleSubmit}>
                    <ModalBody>
                        <FormGroup>
                            <Label>Password: </Label>
                            <Input
                                className={
                                    form.touched.password &&
                                    !form.errors.password
                                        ? "border-success"
                                        : ""
                                }
                                type="password"
                                name="password"
                                value={form.values.password}
                                onChange={form.handleChange}
                                onBlur={form.handleBlur}
                            />
                        </FormGroup>
                        {form.touched.password && form.errors.password && (
                            <div className={styles.error}>
                                {form.errors.password}
                            </div>
                        )}
                        <FormGroup>
                            <Label>Confirm Password: </Label>
                            <Input
                                className={
                                    form.touched.confirmPassword &&
                                    !form.errors.confirmPassword
                                        ? "border-success"
                                        : ""
                                }
                                type="password"
                                name="confirmPassword"
                                value={form.values.confirmPassword}
                                onChange={form.handleChange}
                                onBlur={form.handleBlur}
                            />
                            {form.touched.confirmPassword &&
                                form.errors.confirmPassword && (
                                    <div className={styles.error}>
                                        {form.errors.confirmPassword}
                                    </div>
                                )}
                            {passwordChanged && (
                                <p
                                    className={styles.success}
                                    style={
                                        passwordChanged.includes("success")
                                            ? { color: "green" }
                                            : { color: "red" }
                                    }
                                >
                                    {passwordChanged}
                                </p>
                            )}
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            type="submit"
                            color="primary"
                            disabled={
                                (!form.dirty || !form.isValid) &&
                                !passwordChanged
                            }
                        >
                            Submit
                        </Button>
                    </ModalFooter>
                </Form>
            </Modal>
            {/* Preferences modal */}
            <Modal
                className={styles.modal}
                isOpen={isPrefModalOpen}
                toggle={togglePrefModal}
            >
                <ModalHeader toggle={togglePrefModal}>Preferences</ModalHeader>
                <ModalBody>
                    <FormGroup switch>
                        <Input
                            type="switch"
                            role="switch"
                            id="pref-run-inline"
                            checked={runInline}
                            onChange={(e) =>
                                setPreference(
                                    "run_inline",
                                    e.target.checked ? "true" : "false"
                                )
                            }
                        />
                        <Label check for="pref-run-inline">
                            Run launch-page jobs inline — stay on the launch page and
                            show a notification instead of redirecting to the
                            Review screen.
                        </Label>
                    </FormGroup>
                    <FormGroup switch>
                        <Input
                            type="switch"
                            role="switch"
                            id="pref-use-new-launch-page"
                            checked={useNewLaunchPage}
                            onChange={(e) =>
                                setPreference(
                                    "use_new_launch_page",
                                    e.target.checked ? "true" : "false"
                                )
                            }
                        />
                        <Label check for="pref-use-new-launch-page">
                            Use New Launch Page
                        </Label>
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={togglePrefModal}>
                        Close
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Hidden file input used for logo upload */}
            {hiddenUploadLogoFileInput}
        </header>
    );
}
